/**
 * Format a positive duration in minutes for display in the player UI.
 *
 * Cases:
 *   >= 48h  → "3 days 4 hours" / "2 days"
 *   >= 1h   → "5h 20m" / "5h"
 *   >= 1m   → "47 min"
 *   < 1m    → "<1 min"
 */
export function formatDurationSmart(minutes: number): string {
	const m = Math.max(0, Math.round(minutes));
	if (m < 1) return "<1 min";

	const dayMin = 24 * 60;
	if (m >= 2 * dayMin) {
		const days = Math.floor(m / dayMin);
		const hours = Math.floor((m % dayMin) / 60);
		const dayPart = `${days} day${days === 1 ? "" : "s"}`;
		if (hours === 0) return dayPart;
		return `${dayPart} ${hours} hour${hours === 1 ? "" : "s"}`;
	}

	if (m >= 60) {
		const hours = Math.floor(m / 60);
		const remaining = m % 60;
		if (remaining === 0) return `${hours}h`;
		return `${hours}h ${remaining}m`;
	}

	return `${m} min`;
}

/**
 * Format a live countdown (in seconds remaining).
 *
 * Below an hour, use M:SS so the seconds tick. Above an hour, fall back
 * to {@link formatDurationSmart} so multi-day timers don't read as
 * "4380:00".
 */
export function formatRemainingSmart(seconds: number): string {
	const s = Math.max(0, Math.round(seconds));
	if (s < 3600) {
		const mins = Math.floor(s / 60);
		const secs = s % 60;
		return `${mins}:${secs.toString().padStart(2, "0")}`;
	}
	return formatDurationSmart(s / 60);
}
